import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Badge, Breadcrumb, Button, Spinner } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import DateRangePicker from '@wojtekmaj/react-daterange-picker';
import { isAfter, isBefore } from 'date-fns';
import SidebarBilling from './sidebar';
import config from '../../../config/Constant';
import { getRequest, downloadFile } from '../../../helpers/apiHandler';
import CustomAlert from '../../../components/CustomAlert';
import IconELFO from '../../../assets/elfo-mascot-logo2.png';
import IconCalendar from '../../../assets/SidebarIcons/icon-calendar-time.svg';
import '@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css';
import 'react-calendar/dist/Calendar.css';

const BillingLog = () => {
  const navigate = useNavigate();
  const { access_token, role } = useSelector((state) => state.auth.user);
  const [onLoading, setOnLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [clickedID, setClickedID] = useState();
  const [billingLogData, setBillingLogData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [date, setDate] = useState([null, null]);

  const [alertState, setAlertState] = useState({
    show: false,
    variant: 'danger',
    message: '',
  });

  const getBillingLog = () => {
    setOnLoading(true);
    const payload = {
      headers: { Authorization: access_token.token },
      params: {},
    };
    getRequest('/v2/billing-log', payload, (res) => {
      if (res.success) {
        const { data } = res;
        setBillingLogData(data);
        setFilteredData(data);
      }
      setOnLoading(false);
    });
  };

  const handleTopupBtn = () => {
    
    if (role === "manager") {
      navigate('/manager/dashboard/payment/buy-credits');
    }

    if (role === "user") {
      navigate('/dashboard/payment/buy-credits');
    }

  };

  const handleResetFilter = () => {
    setDate([null, null]);
    setFilteredData(billingLogData);
  };

  const handleInvoiceDownload = (id) => {
    setClickedID(id);
    setDownloadLoading(true);
    downloadFile(
      {
        url: `/v2/payment/${id}/download-invoice`,
        data: {},
        access_token: access_token,
        fileName: `invoice-${id}.pdf`,
      },
      (res) => {
        if (!res.error) {
          setAlertState({
            message: 'Your invoice was downloaded successfully.',
            show: true,
            variant: 'success',
          });
        } else {
          setAlertState({
            message: 'There was an error downloading your invoice. Please try again.',
            show: true,
            variant: 'danger',
          });
        }
        setDownloadLoading(false);
      }
    );
  };

  const handleSearch = () => {
    if (date[0] !== null && date[1] !== null) {
      const filteredData = billingLogData.filter((item) => {
        const minDate = isAfter(new Date(item.created_at), new Date(date[0]));
        const maxDate = isBefore(new Date(item.created_at), new Date(date[1]));

        return minDate && maxDate;
      });
      setFilteredData(filteredData);
    } else {
      setFilteredData(billingLogData);
    }
  };

  useEffect(() => {
    getBillingLog();
  }, []);

  useEffect(() => {
    handleSearch();
  }, [date]);

  useEffect(() => {
    if (alertState.show) {
      setTimeout(() => {
        setAlertState({
          message: '',
          show: false,
          variant: '',
        });
      }, config.notify_time);
    }
  }, [alertState]);

  const columns = [
    {
      name: 'Status',
      sortable: true,
      center: 'true',
      width: '150px',
      selector: (row) => {
        let html = '';

        if (row.status === 'paid') {
          html = <Badge variant="success">{row.status}</Badge>;
        }
        if (row.status === 'failed') {
          html = <Badge variant="danger">{row.status}</Badge>;
        }
        if (row.status === 'pending') {
          html = <Badge variant="secondary">{row.status}</Badge>;
        }
        if (row.status === 'cancelled') {
          html = <Badge variant="primary">{row.status}</Badge>;
        }

        return html;
      },
    },
    {
      name: 'Date & Time',
      sortable: true,
      selector: (row) => row.created_at,
    },
    {
      name: 'Invoice Number',
      sortable: true,
      selector: (row) =>
        row.status === 'paid' ? (
          <Button
            variant="link"
            className="d-flex align-items-center"
            onClick={() => handleInvoiceDownload(row.id)}
            disabled={row.id === clickedID && downloadLoading}
            style={{ gap: 6 }}
          >
            {row.id === clickedID && downloadLoading && (
              <Spinner animation="border" size="sm" className="mr-2" />
            )}
            EA2P{row.id}
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g id="Icons">
                <path
                  id="Vector"
                  d="M3.33317 14.6663C2.94359 14.6663 2.61008 14.5276 2.33265 14.2502C2.05522 13.9728 1.9165 13.6393 1.9165 13.2497V4.74967C1.9165 4.36009 2.05522 4.02658 2.33265 3.74915C2.61008 3.47172 2.94359 3.33301 3.33317 3.33301H6.87484V4.74967H3.33317V13.2497H14.6665V4.74967H11.1248V3.33301H14.6665C15.0561 3.33301 15.3896 3.47172 15.667 3.74915C15.9445 4.02658 16.0832 4.36009 16.0832 4.74967V13.2497C16.0832 13.6393 15.9445 13.9728 15.667 14.2502C15.3896 14.5276 15.0561 14.6663 14.6665 14.6663H3.33317ZM8.99984 11.408L5.45817 7.86634L6.44984 6.87467L8.2915 8.71634V3.33301H9.70817V8.71634L11.5498 6.87467L12.5415 7.86634L8.99984 11.408Z"
                  fill="#0BA9E0"
                />
              </g>
            </svg>
          </Button>
        ) : (
          ''
        ),
    },
    {
      name: 'Amount',
      sortable: true,
      selector: (row) => row.base_price,
    },
    {
      name: 'Amount (Incl SST)',
      sortable: true,
      selector: (row) => row.user_price,
    },
  ];

  return (
    <>
      <SidebarBilling />
      <div className="BillingLog">
        <div className="page-heading">
          <div className="container-left w-100">
            <div className="page-title">Billing Log</div>
          </div>
          <div className="data-filter w-100 mt-3">
            <div className="container-left">
              <DateRangePicker
                value={date}
                onChange={setDate}
                clearIcon={null}
                calendarIcon={<img src={IconCalendar} alt="..." />}
                format="MM-dd-y"
                rangeDivider={<i className="fas fa-long-arrow-alt-right" />}
                dayPlaceholder="DD"
                monthPlaceholder="MM"
                yearPlaceholder="YYYY"
              />
            </div>
            <div className="container-right">
              <Button variant="ghost" onClick={handleResetFilter} style={{ minWidth: 160 }}>
                Reset
              </Button>
              <Button variant="primary" onClick={handleTopupBtn} style={{ minWidth: 160 }}>
                Top Up
              </Button>
            </div>
          </div>
        </div>
        <CustomAlert
          message={alertState.message}
          show={alertState.show}
          variant={alertState.variant}
          closeAlert={() => setAlertState({ show: false, variant: 'danger', message: '' })}
        />
        {filteredData.length > 0 ? (
          <DataTable
            columns={columns}
            data={filteredData}
            className="custom-datatable"
            paginationComponentOptions={{
              rowsPerPageText: 'Items per page:',
            }}
            pagination
          />
        ) : (
          <div className="page-content text-center">
            <img src={IconELFO} alt="..." />
            <h4 className="mt-2">Looks like you have no billing logs</h4>
            <p>No transactions or billing activities have been recorded yet</p>
          </div>
        )}
      </div>
    </>
  );
};


export default BillingLog;
